import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { formatEUR, formatDate, today, parseAmount, invalidDateMessage } from "../lib/format";
import { Plus, Trash, PencilSimple, CaretDown, CaretRight, Copy, CheckCircle, ArrowCounterClockwise, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Modal, StatusBadge } from "./AVGS";
import FilterBar from "../components/FilterBar";
import FilterSummary from "../components/FilterSummary";

const SEND_OPTIONS = [
  { key: "post", label: "Post" },
  { key: "fax", label: "Fax" },
  { key: "portal", label: "Portal" },
  { key: "email", label: "E-Mail" },
];

const empty = {
  customer_name: "",
  mass_start_date: today(),
  mass_end_date: "",
  num_installments: 3,
  installment_amount: 0,
  payment_term_days: 20,
  sent_channels: [],
  sent_date: "",
  manually_paid: false,
  paid_date: "",
  ue_planned: 0,
  personnel_cost_per_ue: 0,
  material_cost_flat: 0,
  room_cost_flat: 0,
  notes: "",
};

export default function FBWPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // Filters
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterMonthField, setFilterMonthField] = useState("mass_start_date");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/fbw");
    setItems(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      customer_name: it.customer_name || "",
      mass_start_date: it.mass_start_date || today(),
      mass_end_date: it.mass_end_date || "",
      num_installments: it.num_installments || 1,
      installment_amount: it.installment_amount || 0,
      payment_term_days: it.payment_term_days || 20,
      sent_channels: it.sent_channels || [],
      sent_date: it.sent_date || "",
      manually_paid: !!it.manually_paid,
      paid_date: it.paid_date || "",
      ue_planned: it.ue_planned ?? 0,
      personnel_cost_per_ue: it.personnel_cost_per_ue ?? 0,
      material_cost_flat: it.material_cost_flat ?? 0,
      room_cost_flat: it.room_cost_flat ?? 0,
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const toggleChannel = (key) => {
    setForm((f) => {
      const has = (f.sent_channels || []).includes(key);
      const next = has ? f.sent_channels.filter((k) => k !== key) : [...(f.sent_channels || []), key];
      return { ...f, sent_channels: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const dateErr = invalidDateMessage(form, [["mass_start_date", "Kurs-Start"], ["mass_end_date", "Kurs-Ende"], ["sent_date", "Versanddatum"], ["paid_date", "Zahlungsdatum"]]);
    if (dateErr) { toast.error(dateErr); return; }
    try {
      const payload = {
        ...form,
        customer_name: (form.customer_name || "").replace(/\s+/g, " ").trim(),
        num_installments: parseInt(form.num_installments) || 1,
        installment_amount: parseAmount(form.installment_amount || 0),
        payment_term_days: parseInt(form.payment_term_days) || 20,
        ue_planned: parseAmount(form.ue_planned || 0),
        personnel_cost_per_ue: parseAmount(form.personnel_cost_per_ue || 0),
        material_cost_flat: parseAmount(form.material_cost_flat || 0),
        room_cost_flat: parseAmount(form.room_cost_flat || 0),
      };
      if (editing) {
        await api.put(`/fbw/${editing}`, payload);
        toast.success("FBW-Kurs aktualisiert");
      } else {
        await api.post("/fbw", payload);
        toast.success("FBW-Kurs angelegt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/fbw/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const markInstallment = async (courseId, idx, body) => {
    try {
      await api.patch(`/fbw/${courseId}/installment/${idx}`, body);
      toast.success(body.paid ? `Abschlag #${idx} als bezahlt markiert` : `Abschlag #${idx} zurückgesetzt`);
      load();
    } catch (e) {
      toast.error("Konnte Abschlag-Status nicht aktualisieren");
    }
  };

  const total = (it) => (it.num_installments || 0) * (it.installment_amount || 0);

  const filteredItems = items.filter((it) => {
    if (search) {
      const q = search.toLowerCase().trim();
      if (!(it.customer_name || "").toLowerCase().includes(q)) return false;
    }
    if (filterMonth && filterMonth !== "all") {
      const fieldVal = it[filterMonthField];
      if (!fieldVal || !String(fieldVal).startsWith(filterMonth)) return false;
    }
    if (filterStatus !== "all") {
      if ((it.status || "open") !== filterStatus) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="fbw-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">FBW</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">Kurse mit Abschlagsrechnungen</h1>
          <p className="text-sm text-[#5B6371] mt-1">
            Teilnehmer, Maßnahme-Zeitraum, Anzahl & Preis der monatlichen Abschläge eingeben — System berechnet automatisch die erwarteten Zahlungseingänge.
          </p>
        </div>
        <button onClick={openCreate}
          data-testid="fbw-create-button"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5">
          <Plus size={16} weight="bold" /> Neuer FBW-Kurs
        </button>
      </header>

      <FilterBar
        items={items}
        search={search} setSearch={setSearch}
        filterMonth={filterMonth} setFilterMonth={setFilterMonth}
        filterMonthField={filterMonthField} setFilterMonthField={setFilterMonthField}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        monthFieldOptions={[
          { value: "mass_start_date", label: "Maßnahme-Start" },
          { value: "mass_end_date", label: "Maßnahme-Ende" },
        ]}
      />

      <FilterSummary items={filteredItems} kind="fbw" />

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fbw-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="w-8 px-2 py-3"></th>
                <th className="text-left px-4 py-3 font-medium">Teilnehmer</th>
                <th className="text-left px-4 py-3 font-medium">Maßnahme</th>
                <th className="text-left px-4 py-3 font-medium">Abschläge</th>
                <th className="text-right px-4 py-3 font-medium">Gesamt</th>
                <th className="text-left px-4 py-3 font-medium">Versand</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>)}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#94A3B8]">
                  {items.length === 0 ? "Noch keine FBW-Kurse." : "Keine Treffer für die aktuellen Filter."}
                </td></tr>
              )}
              {filteredItems.map((it) => (
                <>
                  <tr key={it.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`fbw-row-${it.id}`}>
                    <td className="px-2 py-3 text-center">
                      <button onClick={() => setExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))}
                        className="text-[#5B6371] hover:text-[#0AA09E]" data-testid={`fbw-expand-${it.id}`}>
                        {expanded[it.id] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium">{it.customer_name}</td>
                    <td className="px-4 py-3 text-xs text-[#5B6371]">
                      {formatDate(it.mass_start_date)} – {formatDate(it.mass_end_date)}
                      <div className="text-[10px] text-[#94A3B8] mt-0.5">Abschläge: monatlich ohne Rechnung</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col gap-1.5" data-testid={`fbw-installments-summary-${it.id}`}>
                        {(it.schedule || []).map((s) => {
                          const today = new Date().toISOString().slice(0, 10);
                          const isOverdue = !s.paid && s.expected_payment_date < today;
                          const displayAmt = (s.actual_amount !== undefined && s.actual_amount !== null) ? s.actual_amount : s.amount;
                          return (
                            <div key={s.index}
                              className={`flex items-center gap-2 text-[11px] rounded px-2 py-1 ${
                                s.paid ? "bg-[#E6F7F7] text-[#0AA09E]" :
                                isOverdue ? "bg-[#FFF1EE] text-[#FF6B5C]" :
                                "bg-[#F8FAFC] text-[#5B6371]"
                              }`}
                              data-testid={`fbw-rate-${it.id}-${s.index}`}
                              title={s.paid ? `Rate ${s.index} bezahlt am ${formatDate(s.paid_date)}` :
                                isOverdue ? `Rate ${s.index} überfällig (erwartet ${formatDate(s.expected_payment_date)})` :
                                `Rate ${s.index} erwartet am ${formatDate(s.expected_payment_date)}`}>
                              <span className="font-semibold">#{s.index}</span>
                              <span className="font-medium">{formatEUR(displayAmt)}</span>
                              <span className="ml-auto">
                                {s.paid ? `✓ ${formatDate(s.paid_date)}` :
                                 isOverdue ? `⚠ ${formatDate(s.expected_payment_date)}` :
                                 `→ ${formatDate(s.expected_payment_date)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-[#94A3B8] mt-1.5 text-center">{it.num_installments}× ⌀ {formatEUR(it.installment_amount)}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatEUR(total(it))}</td>
                    <td className="px-4 py-3">
                      {(it.sent_channels && it.sent_channels.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {it.sent_channels.map((c) => (
                            <span key={c} className="text-[10px] uppercase tracking-wider font-medium bg-[#E6F7F7] text-[#0AA09E] px-1.5 py-0.5 rounded">
                              {c === "post" ? "Post" : c === "fax" ? "Fax" : c === "portal" ? "Portal" : "Mail"}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[10px] text-[#94A3B8]">— nicht gesendet</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={it.status} />
                      {it.manually_paid && it.paid_date && (
                        <div className="text-[10px] text-[#94A3B8] mt-0.5">am {formatDate(it.paid_date)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" data-testid={`fbw-edit-${it.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => onDelete(it.id)} className="text-[#5B6371] hover:text-[#FF6B5C] p-1.5" data-testid={`fbw-delete-${it.id}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                  {expanded[it.id] && (
                    <tr key={`${it.id}-sched`} className="bg-[#F8FAFC] border-b border-[#F1F5F9]">
                      <td></td>
                      <td colSpan={7} className="px-4 py-3">
                        <div className="label-eyebrow mb-2">Zahlungsplan ({it.schedule?.length || 0} Abschläge) — Klick auf einen Abschlag, um Plan-Datum/-Betrag oder den tatsächlichen Eingang zu erfassen</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {(it.schedule || []).map((s) => (
                            <InstallmentCard
                              key={s.index}
                              courseId={it.id}
                              inst={s}
                              onMark={markInstallment}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
            {filteredItems.length > 0 && (
              <tfoot>
                <tr className="bg-[#F8FAFC] border-t-2 border-[#0AA09E] font-display">
                  <td></td>
                  <td className="px-4 py-3 text-sm font-bold" colSpan={3}>
                    Summe ({filteredItems.length} {filteredItems.length === 1 ? "Kurs" : "Kurse"})
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-base text-[#1F242E]">
                    {formatEUR(filteredItems.reduce((s, r) => s + total(r), 0))}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-xs text-[#5B6371]">
                    {(() => {
                      const paid = filteredItems.reduce((s, r) =>
                        s + ((r.manually_paid || r.status === "paid") ? total(r) : 0), 0);
                      const open = filteredItems.reduce((s, r) => s + total(r), 0) - paid;
                      return (
                        <>
                          <div>Bezahlt: <strong className="text-[#0AA09E]">{formatEUR(paid)}</strong></div>
                          <div>Offen: <strong className="text-[#FF6B5C]">{formatEUR(open)}</strong></div>
                        </>
                      );
                    })()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {open && (
        <Modal title={editing ? "FBW-Kurs bearbeiten" : "Neuer FBW-Kurs"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="fbw-form">
            <Field label="Teilnehmer / Kunde">
              <input required className="field" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                data-testid="fbw-customer-name" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Maßnahme Start">
                <input type="date" min="2020-01-01" max="2100-12-31" required className="field" value={form.mass_start_date}
                  onChange={(e) => setForm({ ...form, mass_start_date: e.target.value })}
                  data-testid="fbw-start-date" />
              </Field>
              <Field label="Maßnahme Ende">
                <input type="date" min="2020-01-01" max="2100-12-31" required className="field" value={form.mass_end_date}
                  onChange={(e) => setForm({ ...form, mass_end_date: e.target.value })}
                  data-testid="fbw-end-date" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Anzahl Abschläge">
                <input type="number" min="1" required className="field" value={form.num_installments}
                  onChange={(e) => setForm({ ...form, num_installments: e.target.value })}
                  data-testid="fbw-num-installments" />
                {(() => {
                  // Auto-Vorschlag basierend auf Kursdauer (jede 30 Tage = 1 Abschlag)
                  if (!form.mass_start_date || !form.mass_end_date) return null;
                  const s = new Date(form.mass_start_date);
                  const e = new Date(form.mass_end_date);
                  if (!(s < e)) return null;
                  const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
                  const suggested = Math.max(1, Math.ceil(days / 30));
                  const current = parseInt(form.num_installments) || 0;
                  if (suggested === current) {
                    return <div className="text-[10px] text-[#0AA09E] mt-1">✓ passt zur Kursdauer ({days} Tage)</div>;
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, num_installments: suggested })}
                      className="text-[10px] text-[#0AA09E] underline mt-1 hover:text-[#087C7B]"
                      data-testid="fbw-num-suggest"
                    >
                      Vorschlag: {suggested} Abschläge (Kurs läuft {days} Tage — 1 Abschlag je 30 Tage). Klick zum Übernehmen.
                    </button>
                  );
                })()}
              </Field>
              <Field label="Preis pro Abschlag (€)">
                <input type="text" inputMode="decimal" required className="field" value={form.installment_amount}
                  onChange={(e) => setForm({ ...form, installment_amount: e.target.value })}
                  data-testid="fbw-installment-amount" />
              </Field>
            </div>

            {/* Zahlungsplan-Vorschau */}
            {form.mass_start_date && parseInt(form.num_installments) > 0 && (() => {
              const start = new Date(form.mass_start_date);
              const n = parseInt(form.num_installments) || 0;
              const amt = parseAmount(form.installment_amount) || 0;
              const dates = [];
              for (let i = 0; i < Math.min(n, 12); i++) {
                const d = new Date(start);
                d.setMonth(d.getMonth() + i + 1);
                dates.push(d.toLocaleDateString("de-DE", { month: "short", year: "2-digit", day: "2-digit" }));
              }
              return (
                <div className="bg-[#E6F7F7] border border-[#0AA09E]/30 rounded-md p-3 text-xs">
                  <div className="label-eyebrow mb-1.5 text-[#0AA09E]">Vorschau — Zahlungsplan</div>
                  <div className="flex flex-wrap gap-1.5">
                    {dates.map((d, i) => (
                      <div key={i} className="bg-white border border-[#0AA09E]/30 rounded px-2 py-1 text-[11px]">
                        <span className="text-[#94A3B8]">#{i + 1}</span>{" "}
                        <strong>{d}</strong>{" "}
                        <span className="text-[#0AA09E]">{formatEUR(amt)}</span>
                      </div>
                    ))}
                    {n > 12 && <div className="text-[10px] text-[#94A3B8] px-2 py-1">…+{n - 12} weitere</div>}
                  </div>
                </div>
              );
            })()}

            <div className="bg-[#FFF1EE] border border-[#FFD9D2] rounded-md p-3 text-xs text-[#5B6371]">
              <strong className="text-[#1F242E]">Hinweis:</strong> FBW-Abschläge kommen <strong>automatisch monatlich ohne Rechnung</strong>.
              1. Abschlag = einen Monat nach Maßnahme-Start, dann jeweils einen Monat später.
              Sobald das Datum erreicht ist, wird der Betrag automatisch als Eingang im Dashboard gezählt.
            </div>

            <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-3 text-sm">
              <div className="label-eyebrow mb-1">Gesamtsumme</div>
              <div className="font-display text-2xl font-bold text-[#0AA09E]">
                {formatEUR((parseAmount(form.installment_amount) || 0) * (parseInt(form.num_installments) || 0))}
              </div>
              <div className="text-xs text-[#5B6371] mt-1">
                {form.num_installments || 0}× {formatEUR(form.installment_amount || 0)} — monatlich ab Maßnahme-Start
              </div>
            </div>

            {/* Deckungsbeitrags-Kalkulation (direkte Kosten) */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-3">Direkte Kosten (für Deckungsbeitrag)</div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Field label="UE geplant (gesamt)">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.ue_planned}
                    onChange={(e) => setForm({ ...form, ue_planned: e.target.value })}
                    data-testid="fbw-ue-planned" placeholder="0" />
                </Field>
                <Field label="Personal €/UE">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.personnel_cost_per_ue}
                    onChange={(e) => setForm({ ...form, personnel_cost_per_ue: e.target.value })}
                    data-testid="fbw-personnel-cost-per-ue" placeholder="0,00" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Material pauschal (€)">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.material_cost_flat}
                    onChange={(e) => setForm({ ...form, material_cost_flat: e.target.value })}
                    data-testid="fbw-material-cost" placeholder="0,00" />
                </Field>
                <Field label="Raum pauschal (€)">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.room_cost_flat}
                    onChange={(e) => setForm({ ...form, room_cost_flat: e.target.value })}
                    data-testid="fbw-room-cost" placeholder="0,00" />
                </Field>
              </div>
              {(() => {
                const ue = parseAmount(form.ue_planned || 0);
                const pers = parseAmount(form.personnel_cost_per_ue || 0) * ue;
                const mat = parseAmount(form.material_cost_flat || 0);
                const room = parseAmount(form.room_cost_flat || 0);
                const direct = pers + mat + room;
                const revenue = (parseAmount(form.installment_amount) || 0) * (parseInt(form.num_installments) || 0);
                const cm = revenue - direct;
                const cmPct = revenue > 0 ? (cm / revenue * 100) : 0;
                return (
                  <div className="mt-2 text-xs text-[#5B6371] bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-2">
                    Direkt gesamt: <strong>{direct.toFixed(2)} €</strong> · Rohdeckungsbeitrag: <strong className={cm >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}>{cm.toFixed(2)} €</strong> ({cmPct.toFixed(1)} %)
                  </div>
                );
              })()}
            </div>

            <Field label="Notizen">
              <textarea className="field" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="fbw-notes" />
            </Field>

            {/* Rechnungsversand */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-2">Rechnungsversand</div>
              <div className="flex flex-wrap gap-3 mb-3">
                {SEND_OPTIONS.map((opt) => {
                  const checked = (form.sent_channels || []).includes(opt.key);
                  return (
                    <label key={opt.key} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${
                      checked ? "bg-[#E6F7F7] border-[#0AA09E] text-[#0AA09E]" : "bg-white border-[#E5E7EB] text-[#5B6371] hover:border-[#0AA09E]"
                    }`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleChannel(opt.key)}
                        data-testid={`fbw-sent-${opt.key}`} className="accent-[#0AA09E]" />
                      <span>per {opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <Field label="Versanddatum (optional)">
                <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.sent_date}
                  onChange={(e) => setForm({ ...form, sent_date: e.target.value })}
                  data-testid="fbw-sent-date" />
              </Field>
            </div>

            {/* Zahlung */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-2">Zahlung</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.manually_paid}
                  onChange={(e) => setForm({ ...form, manually_paid: e.target.checked, paid_date: e.target.checked ? (form.paid_date || today()) : "" })}
                  data-testid="fbw-manually-paid"
                  className="accent-[#0AA09E]" />
                <span className="font-medium">Kurs komplett bezahlt</span>
              </label>
              {form.manually_paid && (
                <div className="mt-3">
                  <Field label="Zahlungseingang am">
                    <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.paid_date}
                      onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                      data-testid="fbw-paid-date" />
                  </Field>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-[#5B6371]" data-testid="fbw-cancel">Abbrechen</button>
              <button type="submit" className="px-5 py-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md" data-testid="fbw-submit">Speichern</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}


function InstallmentCard({ courseId, inst, onMark }) {
  const [editing, setEditing] = useState(false);
  const [planDate, setPlanDate] = useState(inst.expected_payment_date);
  const [planAmt, setPlanAmt] = useState(String(inst.amount));
  const [paidDate, setPaidDate] = useState(inst.paid_date || inst.expected_payment_date);
  const [actualAmt, setActualAmt] = useState(
    inst.actual_amount !== undefined && inst.actual_amount !== null
      ? String(inst.actual_amount)
      : String(inst.amount)
  );
  const [notes, setNotes] = useState(inst.paid_notes || "");

  const isPaid = inst.paid;
  const planDateOverridden = inst.planned_date_override;
  const planAmtOverridden = inst.planned_amount_override;
  const overrideAmt = inst.actual_amount;
  const displayAmt = (overrideAmt !== undefined && overrideAmt !== null) ? overrideAmt : inst.amount;
  const displayDate = inst.paid_date || inst.expected_payment_date;
  const isCombined = isPaid && overrideAmt === 0;
  const isPartial = isPaid && overrideAmt !== null && overrideAmt !== undefined && overrideAmt !== 0 && overrideAmt < inst.amount;
  const isExtra = isPaid && overrideAmt !== null && overrideAmt !== undefined && overrideAmt > inst.amount;

  const savePaid = async () => {
    const body = {
      paid: true,
      paid_date: paidDate,
      actual_amount: actualAmt === "" ? null : parseAmount(actualAmt),
      notes: notes,
    };
    // Plan-Werte auch mitsenden falls vom User geändert
    if (planDate && planDate !== inst.default_planned_date) body.planned_date = planDate;
    const planAmtParsed = planAmt === "" ? null : parseAmount(planAmt);
    if (planAmtParsed !== null && planAmtParsed !== inst.default_planned_amount) {
      body.planned_amount = planAmtParsed;
    }
    await onMark(courseId, inst.index, body);
    setEditing(false);
  };
  const savePlanOnly = async () => {
    const body = {};
    if (planDate && planDate !== inst.default_planned_date) {
      body.planned_date = planDate;
    } else if (planDateOverridden && planDate === inst.default_planned_date) {
      body.planned_date = ""; // reset to default
    }
    const planAmtParsed = planAmt === "" ? null : parseAmount(planAmt);
    if (planAmtParsed !== null) body.planned_amount = planAmtParsed;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    await onMark(courseId, inst.index, body);
    setEditing(false);
  };
  const reset = async () => {
    if (!window.confirm(`Abschlag #${inst.index} zurücksetzen?`)) return;
    await onMark(courseId, inst.index, { paid: false });
    setEditing(false);
  };
  const resetPlan = async () => {
    if (!window.confirm(`Plan-Überschreibung für Abschlag #${inst.index} entfernen?`)) return;
    await onMark(courseId, inst.index, { planned_date: "", planned_amount: inst.default_planned_amount });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="bg-white border-2 border-[#0AA09E] rounded-md p-3 shadow-sm" data-testid={`fbw-inst-edit-${courseId}-${inst.index}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#0AA09E]">Abschlag #{inst.index} bearbeiten</div>
          <button onClick={() => setEditing(false)} className="text-[#94A3B8] hover:text-[#1F242E]">
            <X size={14} />
          </button>
        </div>

        {/* Plan-Sektion */}
        <div className="border border-[#E5E7EB] rounded p-2 mb-2 bg-[#F8FAFC]">
          <div className="text-[9px] uppercase tracking-wider font-bold text-[#5B6371] mb-1.5">📅 Geplanter Eingang</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[#5B6371]">Plan-Datum</label>
              <input type="date" min="2020-01-01" max="2100-12-31" className="field !py-1.5 !text-xs" value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
                data-testid={`fbw-inst-plan-date-${courseId}-${inst.index}`} />
            </div>
            <div>
              <label className="text-[10px] text-[#5B6371]">Plan-Betrag (€)</label>
              <input type="text" inputMode="decimal" className="field !py-1.5 !text-xs" value={planAmt}
                onChange={(e) => setPlanAmt(e.target.value)}
                data-testid={`fbw-inst-plan-amount-${courseId}-${inst.index}`} />
            </div>
          </div>
          <div className="flex justify-between items-center mt-1.5">
            <p className="text-[9px] text-[#94A3B8]">
              Auto-Plan: {formatDate(inst.default_planned_date)} · {formatEUR(inst.default_planned_amount)}
              {(planDateOverridden || planAmtOverridden) && <strong className="text-[#0AA09E] ml-1">(überschrieben)</strong>}
            </p>
            {!isPaid && (planDate !== inst.expected_payment_date || planAmt !== String(inst.amount)) && (
              <button type="button" onClick={savePlanOnly}
                className="text-[10px] bg-[#0AA09E]/10 hover:bg-[#0AA09E]/20 text-[#0AA09E] font-medium px-2 py-0.5 rounded"
                data-testid={`fbw-inst-save-plan-${courseId}-${inst.index}`}>
                Nur Plan speichern
              </button>
            )}
            {(planDateOverridden || planAmtOverridden) && (
              <button type="button" onClick={resetPlan}
                className="text-[10px] text-[#94A3B8] hover:text-[#FF6B5C] px-2 py-0.5"
                title="Auf Auto-Berechnung zurücksetzen">
                ↺ Reset
              </button>
            )}
          </div>
        </div>

        {/* Zahlungseingang-Sektion */}
        <div className="border border-[#E5E7EB] rounded p-2 bg-white">
          <div className="text-[9px] uppercase tracking-wider font-bold text-[#0AA09E] mb-1.5">💰 Tatsächlicher Zahlungseingang</div>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] text-[#5B6371]">Eingang am</label>
              <input type="date" min="2020-01-01" max="2100-12-31" className="field !py-1.5 !text-xs" value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                data-testid={`fbw-inst-paid-date-${courseId}-${inst.index}`} />
            </div>
            <div>
              <label className="text-[10px] text-[#5B6371]">Tatsächlicher Betrag (€)</label>
              <input type="text" inputMode="decimal" className="field !py-1.5 !text-xs" value={actualAmt}
                onChange={(e) => setActualAmt(e.target.value)}
                data-testid={`fbw-inst-amount-${courseId}-${inst.index}`} />
              <p className="text-[9px] text-[#94A3B8] mt-0.5">
                Bei Sammelzahlung → <strong>0 €</strong> hier + voller Betrag bei anderem Abschlag
              </p>
            </div>
            <div>
              <label className="text-[10px] text-[#5B6371]">Notiz (optional)</label>
              <input type="text" className="field !py-1.5 !text-xs" value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="z.B. Zusammen mit Abschlag 2"
                data-testid={`fbw-inst-notes-${courseId}-${inst.index}`} />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={savePaid}
                className="flex-1 bg-[#0AA09E] hover:bg-[#088987] text-white text-xs font-medium py-1.5 rounded"
                data-testid={`fbw-inst-save-${courseId}-${inst.index}`}>
                ✓ Bezahlt speichern
              </button>
              {isPaid && (
                <button onClick={reset}
                  className="text-[#FF6B5C] hover:bg-[#FFF1EE] text-xs font-medium py-1.5 px-2 rounded border border-[#FFE0DA]"
                  title="Zahlung zurücksetzen"
                  data-testid={`fbw-inst-reset-${courseId}-${inst.index}`}>
                  <ArrowCounterClockwise size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact display
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`fbw-inst-card-${courseId}-${inst.index}`}
      className={`text-left rounded-md p-2.5 border transition-all hover:shadow-md hover:scale-[1.01] ${
        isPaid
          ? "bg-[#E6F7F7] border-[#0AA09E]"
          : "bg-white border-[#E5E7EB] hover:border-[#0AA09E]"
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">
          Abschlag #{inst.index}
          {(planDateOverridden || planAmtOverridden) && (
            <span className="ml-1 text-[#0AA09E]" title="Plan manuell überschrieben">✎</span>
          )}
        </div>
        {isPaid && <CheckCircle size={14} weight="fill" className="text-[#0AA09E] shrink-0" />}
      </div>
      <div className="text-sm font-medium mt-0.5 text-[#1F242E]">
        {isCombined ? <span className="text-[#94A3B8] italic text-xs">Sammelzahlung (0 €)</span> : formatEUR(displayAmt)}
        {isPartial && <span className="text-[10px] text-[#FF6B5C] ml-1">teil</span>}
        {isExtra && <span className="text-[10px] text-[#0AA09E] ml-1">+ extra</span>}
      </div>
      <div className={`text-[10px] mt-1 font-medium ${isPaid ? "text-[#0AA09E]" : "text-[#5B6371]"}`}>
        {isPaid ? "✓ Eingang: " : "→ Erwartet: "}{formatDate(displayDate)}
      </div>
      {isPaid && inst.paid_date && inst.paid_date !== inst.expected_payment_date && (
        <div className="text-[9px] text-[#94A3B8] mt-0.5">Plan war: {formatDate(inst.expected_payment_date)}</div>
      )}
      {isPaid && inst.paid_notes && (
        <div className="text-[10px] text-[#5B6371] mt-1 italic truncate" title={inst.paid_notes}>{inst.paid_notes}</div>
      )}
    </button>
  );
}
